import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';
import { AccountClientService } from '@automatick/shared-client-ng';
import {
  AppRoute,
  WorkspaceAutonomyMode,
  WorkspaceKind,
  WorkspaceMemberStatus,
  WorkspaceRole,
  WorkspacesClientService,
  isPrivilegedRole,
} from '@automatick/shared-ng';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  of,
  throwError,
} from 'rxjs';
import { NavItem } from '../models';
import {
  AppWorkspaceContextRuntime,
  createInitialWorkspaceBootstrapState,
  createSystemAppWorkspaceContextEnvironment,
  type AppWorkspaceContextEnvironment,
  type AppWorkspaceContextFetchResult,
} from './app-workspace-context.runtime';
import { WEB_STORAGE } from './token';

const ACTIVE_WORKSPACE_STORAGE_KEY = 'automatick.activeWorkspaceId';

export const APP_WORKSPACE_CONTEXT_ENVIRONMENT =
  new InjectionToken<AppWorkspaceContextEnvironment>(
    'APP_WORKSPACE_CONTEXT_ENVIRONMENT',
    { factory: createSystemAppWorkspaceContextEnvironment },
  );

export interface WorkspaceBootstrapState {
  readonly isLoading: boolean;
  readonly isLoaded: boolean;
  readonly errorMessage: string;
  readonly user: WorkspaceBootstrapUser | null;
  readonly workspaces: readonly WorkspaceBootstrapWorkspace[];
  readonly activeWorkspaceId: string | null;
  readonly activeRole: WorkspaceRole | null;
  readonly activeWorkspaceType: WorkspaceKind | null;
}

export type WorkspaceAgentConfirmationMode =
  'Ask' | 'AskThenAutoProceedIfNoResponse' | 'ProceedAutomatically';

export interface WorkspaceBootstrapUserSettings {
  readonly agentConfirmationMode: WorkspaceAgentConfirmationMode;
}

export interface WorkspaceBootstrapUser {
  readonly userId: string;
  readonly email: string;
  readonly phone: string | null;
  readonly username: string;
  readonly displayName: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly avatarUrl: string | null;
  readonly sessionId: string;
  readonly workspaceId: string | null;
  readonly role: WorkspaceRole | null;
  readonly isAuthenticated: true;
  readonly settings: WorkspaceBootstrapUserSettings;
}

export interface WorkspaceBootstrapWorkspace {
  readonly workspaceId: string;
  readonly slug: string;
  readonly name: string;
  readonly workspaceType: WorkspaceKind;
  readonly role: WorkspaceRole;
  readonly status: WorkspaceMemberStatus;
  readonly defaultAutonomyMode: WorkspaceAutonomyMode;
  readonly timeZone: string;
  readonly isActive: boolean;
  readonly isDefaultWorkspace: boolean;
  readonly joinedOn: string;
}

const BASE_NAVIGATION: readonly NavItem[] = Object.freeze([
  { label: 'Home', route: AppRoute.Home, icon: 'home' },
  { label: 'Work Requests', route: AppRoute.WorkRequests, icon: 'work' },
  {
    label: 'Notifications',
    route: AppRoute.Notifications,
    icon: 'notification',
  },
  { label: 'Channels', route: AppRoute.Channel, icon: 'messaging' },
  { label: 'Marketplace', route: AppRoute.Marketplace, icon: 'sparkle-fill' },
  { label: 'Agents', route: AppRoute.Agents, icon: 'dashboard-circle' },
  { label: 'Workspaces', route: AppRoute.Workspaces, icon: 'workspace' },
  { label: 'Account', route: AppRoute.Account, icon: 'account-circle' },
]);

const PRIVILEGED_NAVIGATION: readonly NavItem[] = Object.freeze([
  ...BASE_NAVIGATION.slice(0, 3),
  { label: 'Approvals', route: AppRoute.Approvals, icon: 'note-approve' },
  ...BASE_NAVIGATION.slice(3),
  { label: 'Credentials', route: AppRoute.Credentials, icon: 'padlock' },
  { label: 'Connectors', route: AppRoute.Connectors, icon: 'hub' },
  {
    label: 'Operations',
    route: AppRoute.Operations,
    icon: 'dashboard-rounded',
  },
]);

const MEMBER_SIDEBAR: readonly NavItem[] = Object.freeze([
  { label: 'Work Requests', route: AppRoute.WorkRequests, icon: 'work' },
  {
    label: 'Notifications',
    route: AppRoute.Notifications,
    icon: 'notification',
  },
  { label: 'Channels', route: AppRoute.Channel, icon: 'messaging' },
  { label: 'Marketplace', route: AppRoute.Marketplace, icon: 'sparkle-fill' },
  { label: 'Agents', route: AppRoute.Agents, icon: 'dashboard-circle' },
]);

const PRIVILEGED_SIDEBAR: readonly NavItem[] = Object.freeze([
  ...MEMBER_SIDEBAR.slice(0, 2),
  { label: 'Approvals', route: AppRoute.Approvals, icon: 'note-approve' },
  { label: 'Connectors', route: AppRoute.Connectors, icon: 'hub' },
  ...MEMBER_SIDEBAR.slice(2),
]);

@Injectable({ providedIn: 'root' })
export class AppStateService implements OnDestroy {
  private readonly accountClient = inject(AccountClientService);
  private readonly workspacesClient = inject(WorkspacesClientService);
  private readonly storage = inject(WEB_STORAGE);
  private readonly environment = inject(APP_WORKSPACE_CONTEXT_ENVIRONMENT);
  private readonly stateSubject = new BehaviorSubject<WorkspaceBootstrapState>(
    createInitialWorkspaceBootstrapState(),
  );
  private readonly runtime = new AppWorkspaceContextRuntime(
    {
      fetchContext: () =>
        forkJoin({
          user: this.accountClient.me(),
          workspaces: this.workspacesClient.listWorkspaces(),
        }).pipe(
          map((response): AppWorkspaceContextFetchResult => ({
            kind: 'context',
            response,
          })),
          catchError((error: unknown) =>
            error instanceof HttpErrorResponse && error.status === 401
              ? of<AppWorkspaceContextFetchResult>({
                  kind: 'unauthenticated',
                })
              : throwError(() => error),
          ),
        ),
      switchWorkspace: (workspaceId) =>
        this.accountClient.switchWorkspace({ body: { workspaceId } }),
    },
    {
      read: () => this.storage.getItem<string>(ACTIVE_WORKSPACE_STORAGE_KEY),
      write: (workspaceId) =>
        this.storage.set(ACTIVE_WORKSPACE_STORAGE_KEY, workspaceId),
      remove: () => this.storage.remove(ACTIVE_WORKSPACE_STORAGE_KEY),
    },
    { publish: (state) => this.stateSubject.next(state) },
    this.environment,
  );

  get state$() {
    return this.stateSubject.asObservable();
  }

  getStateSnapshot(): WorkspaceBootstrapState {
    return this.stateSubject.value;
  }

  bootstrapWorkspaceContext(force = false): void {
    this.runtime.bootstrap(force);
  }

  switchWorkspace(workspaceId: string): void {
    this.runtime.switchWorkspace(workspaceId);
  }

  getPrimaryNavigation(role: WorkspaceRole | null | undefined): NavItem[] {
    return cloneNavigation(
      isPrivilegedRole(role) ? PRIVILEGED_NAVIGATION : BASE_NAVIGATION,
    );
  }

  getSidebarNavigation(role: WorkspaceRole | null | undefined): NavItem[] {
    return cloneNavigation(
      isPrivilegedRole(role) ? PRIVILEGED_SIDEBAR : MEMBER_SIDEBAR,
    );
  }

  getSidebarSecondaryNavigation(
    role: WorkspaceRole | null | undefined,
  ): NavItem[] {
    const items: NavItem[] = [
      { label: 'Workspaces', route: AppRoute.Workspaces, icon: 'workspace' },
      { label: 'Account', route: AppRoute.Account, icon: 'account-circle' },
    ];
    if (isPrivilegedRole(role)) {
      items.push({
        label: 'Operations',
        route: AppRoute.Operations,
        icon: 'dashboard-rounded',
      });
    }
    return items;
  }

  getMenuNavigation(role: WorkspaceRole | null | undefined): NavItem[] {
    const sidebarRoutes = new Set(
      this.getSidebarNavigation(role).map((item) => item.route),
    );
    return this.getPrimaryNavigation(role).filter(
      (item) => !sidebarRoutes.has(item.route) && item.route !== AppRoute.Home,
    );
  }

  ngOnDestroy(): void {
    this.runtime.destroy();
    this.stateSubject.complete();
  }
}

function cloneNavigation(items: readonly NavItem[]): NavItem[] {
  return items.map((item) => ({ ...item }));
}
